import {noop} from 'lodash';
import memoize from 'lru-memoize';
import {DateTime} from 'luxon';
import React, {Component, createRef, RefObject} from 'react';
import styled from 'styled-components';
import {Date as SugarDate} from 'sugar';
import {Key} from 'ts-key-enum';

import {defineMessage, Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {FrontKeyboardEvent, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {CheckBox} from '../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {BoxedInput} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {BoxedInputChildProps} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {
  InternationalizationContextProps,
  withInternationalization,
} from '../../../../srcCommon/internationalization/internationalizationContext';
import {formatDate} from '../../../helpers/dateTimeHelpers';
import {DatePickerContainer} from './datePickerContainer';

/*
 * Props.
 */

interface DateComboBoxProps extends InternationalizationContextProps {
  selectedDateTime?: DateTime;
  onDateSelect: HandlerOf<DateTime>;
  dateFormat?: Intl.DateTimeFormatOptions;
  isMultiDay?: boolean;
  onMultiDayToggle?: HandlerOf<boolean>;
  className?: string;
  shouldFocusInput?: boolean;
}

/*
 * Styles.
 */

const StyledContentDiv = styled.div`
  padding-top: 12px;
`;

const StyledDatePickerWrapperDiv = styled.div`
  padding: 0px 16px 12px 16px;
`;

const MultiDaySectionDiv = styled.div`
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  height: 40px;
  display: flex;
  padding: 0px 14px;
  align-items: center;
`;

/*
 * Component.
 */

interface DateComboBoxState {
  isInputFocused: boolean;
  value: string;
  selectedDateTime?: DateTime;
}

class DateComboBoxComponent extends Component<DateComboBoxProps, DateComboBoxState> {
  constructor(props: DateComboBoxProps) {
    super(props);

    const value = formatDate(props.localize, props.selectedDateTime, this.props.dateFormat);
    this.state = {isInputFocused: false, value, selectedDateTime: props.selectedDateTime};

    this.inputRef = createRef<BoxedInput>();
    this.boxRef = createRef<HTMLDivElement>();
  }

  private readonly inputRef: RefObject<BoxedInput>;
  private readonly boxRef: RefObject<HTMLDivElement>;

  static getDerivedStateFromProps(props: Readonly<DateComboBoxProps>, state: Readonly<DateComboBoxState>) {
    const {selectedDateTime} = props;

    if (selectedDateTime === state.selectedDateTime) {
      return null;
    }

    const newValue = formatDate(props.localize, selectedDateTime, props.dateFormat);

    return {
      value: newValue,
      selectedDateTime,
    };
  }

  /*
   * Input event handlers.
   */

  private onChange: HandlerOf<string> = (value) => {
    this.setState({value});
  };

  private onFocus = () => {
    this.setState({isInputFocused: true});
  };

  private onKeyDown: HandlerOf<FrontKeyboardEvent> = (event) => {
    if (event.key !== Key.Enter) {
      return;
    }

    if (!this.inputRef.current) {
      return;
    }

    this.inputRef.current.blur();
  };

  private onBlur = () => {
    this.setState({isInputFocused: false});

    const parsedTime = this.tryParseValue(this.state.value, this.state.selectedDateTime);

    // If parsing failed, revert to the last valid selectedDateTime.
    if (!parsedTime) {
      this.setState({
        value: formatDate(this.props.localize, this.props.selectedDateTime, this.props.dateFormat),
      });
      return;
    }

    // No change needed if the date has not changed.
    if (parsedTime.toISODate() === this.props.selectedDateTime?.toISODate()) {
      return;
    }

    // Inform the parent of the date change.
    this.props.onDateSelect(parsedTime);
  };

  private readonly tryParseValue = memoize()((value: string, selectedDateTime?: DateTime) => {
    const {i18n} = this.props;

    if (!selectedDateTime) {
      return undefined;
    }

    // Default to the currently selected year in case `value` does not contain a year.
    const sugarDate = SugarDate.create(value, {
      locale: i18n.locale,
      params: {
        year: selectedDateTime.year,
      },
    });
    const parsedDateTime = DateTime.fromJSDate(sugarDate);

    return (parsedDateTime.isValid && parsedDateTime) || undefined;
  });

  /*
   * Dropdown event handlers.
   */

  private readonly onDateSelect: HandlerOf<DateTime> = (dateTime) => {
    // We need a ref to the input at this point.
    const currentInput = this.inputRef.current;
    if (!currentInput) {
      return;
    }

    // Inform the parent of the change.
    this.props.onDateSelect(dateTime);

    // Blur the element.
    currentInput.blur();
  };

  /*
   * Render.
   */

  render() {
    return (
      <>
        <BoxedInput ref={this.inputRef} boxRef={this.boxRef} boxClassName={this.props.className}>
          {(childProps) => this.renderInputContent(childProps)}
        </BoxedInput>
        {this.state.isInputFocused && this.renderDropdown()}
      </>
    );
  }

  private renderInputContent(childProps: BoxedInputChildProps) {
    const {localize} = this.props;
    return (
      <BoxedInputElement
        {...childProps}
        onChange={this.onChange}
        onFocus={this.onFocus}
        onKeyDown={this.onKeyDown}
        onBlur={this.onBlur}
        placeholder={localize(
          defineMessage({
            id: 'date-combobox-placeholder',
            message: 'mm/dd/yyyy',
          }),
        )}
        type={BoxedInputElementTypesEnum.TEXT}
        value={this.state.value}
        shouldFocus={this.props.shouldFocusInput}
      />
    );
  }

  private renderDropdown() {
    return (
      <Dropdown {...this.props} anchor={this.boxRef} isExclusive={false}>
        <StyledContentDiv onMouseDown={(e) => e.preventDefault()}>
          <StyledDatePickerWrapperDiv>
            <DatePickerContainer
              selectedDate={this.tryParseValue(this.state.value, this.state.selectedDateTime)}
              onDateSelect={this.onDateSelect}
            />
          </StyledDatePickerWrapperDiv>
          {this.maybeRenderMultiDayToggle()}
        </StyledContentDiv>
      </Dropdown>
    );
  }

  private maybeRenderMultiDayToggle() {
    const {onMultiDayToggle, isMultiDay} = this.props;

    if (!onMultiDayToggle) {
      return null;
    }

    return (
      <MultiDaySectionDiv>
        <ChromelessButton onClick={() => onMultiDayToggle(!isMultiDay)}>
          <CheckBox isChecked={isMultiDay} onChange={noop}>
            <Trans id="date-combobox-multiday">Multi-day</Trans>
          </CheckBox>
        </ChromelessButton>
      </MultiDaySectionDiv>
    );
  }
}

export const DateComboBox = withInternationalization(DateComboBoxComponent);
